import { useRef, useState } from 'react';
import { toast } from 'sonner';
import type { UnifiedAsset } from '@/hooks/useAssetsUnified';

export interface AllocationInfo {
  count: number;
  portfolioNames: string[];
}

interface UseAssetDeletionParams {
  filteredAssets: UnifiedAsset[] | undefined;
  selectedAssets: string[];
  setSelectedAssets: (ids: string[]) => void;
  checkPortfolioAllocationsBulk: (assetIds: string[]) => Promise<Record<string, AllocationInfo>>;
  deleteManualAsset: (id: string, cascade?: boolean) => Promise<void>;
  refetch: () => void;
}

export function useAssetDeletion({
  filteredAssets,
  selectedAssets,
  setSelectedAssets,
  checkPortfolioAllocationsBulk,
  deleteManualAsset,
  refetch,
}: UseAssetDeletionParams) {
  // Dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMixedDeleteConfirm, setShowMixedDeleteConfirm] = useState(false);
  const [showCascadeConfirm, setShowCascadeConfirm] = useState(false);

  // Cascade lists and toggles
  const [cascadeBlocked, setCascadeBlocked] = useState<{ id: string; name: string; count: number; portfolios: string[] }[]>([]);
  const [cascadeDeletable, setCascadeDeletable] = useState<{ id: string; name: string }[]>([]);
  const [cascadeShowAll, setCascadeShowAll] = useState(false);

  // Mixed-delete info and subset
  const [mixedDeleteInfo, setMixedDeleteInfo] = useState<{ manual: number; connected: number; account: number }>({ manual: 0, connected: 0, account: 0 });
  const mixedManualAssetsRef = useRef<UnifiedAsset[]>([]);

  // Toast ID to track progress UI
  const deleteToastIdRef = useRef<string>('assets-delete-progress');

  const renderDeleteToast = (completed: number, total: number, currentName?: string) => {
    const pct = total > 0 ? Math.round((completed / Math.max(1, total)) * 100) : 0;
    toast.custom(() => (
      <div className="rounded-md border bg-background p-3 text-sm w-[320px]">
        <div className="font-medium mb-1">Deleting assets…</div>
        <div className="text-xs text-muted-foreground mb-2">
          {completed} of {total} completed{currentName ? ` • ${currentName}` : ''}
        </div>
        <div className="w-full h-2 rounded bg-muted overflow-hidden">
          <div className="h-2 bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
    ), { id: deleteToastIdRef.current, duration: Infinity });
  };

  // Public handlers
  const handleBulkDelete = () => {
    const selectedAssetData = (filteredAssets || []).filter(a => selectedAssets.includes(a.id));
    if ((selectedAssetData?.length || 0) === 0) return;

    const manual = selectedAssetData.filter(a => a.source_type === 'manual');
    const connected = selectedAssetData.filter(a => a.source_type === 'connected');
    const account = selectedAssetData.filter(a => a.source_type === 'account');

    if ((connected.length + account.length) > 0) {
      setMixedDeleteInfo({ manual: manual.length, connected: connected.length, account: account.length });
      mixedManualAssetsRef.current = manual as UnifiedAsset[];
      setShowMixedDeleteConfirm(true);
      return;
    }

    // Pure manual selection: show standard delete confirm
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    const selectedAssetData = (filteredAssets || []).filter(asset => selectedAssets.includes(asset.id));
    const manualAssets = selectedAssetData.filter(a => a.source_type === 'manual');

    if (manualAssets.length === 0) {
      setShowDeleteConfirm(false);
      return;
    }

    if (manualAssets.length !== selectedAssetData.length) {
      toast.error('Cannot delete connected or account assets');
      setShowDeleteConfirm(false);
      return;
    }

    const allocationMap = await checkPortfolioAllocationsBulk(manualAssets.map(a => a.id));

    const blockedNow: { id: string; name: string; count: number; portfolios: string[] }[] = [];
    const deletableNow: { id: string; name: string }[] = [];

    for (const a of manualAssets) {
      const info = allocationMap[a.id];
      if (info && info.count > 0) {
        blockedNow.push({ id: a.id, name: (a as any).asset_name, count: info.count, portfolios: info.portfolioNames });
      } else {
        deletableNow.push({ id: a.id, name: (a as any).asset_name });
      }
    }

    if (blockedNow.length > 0) {
      setCascadeBlocked(blockedNow);
      setCascadeDeletable(deletableNow);
      setCascadeShowAll(false);
      setShowDeleteConfirm(false);
      setShowCascadeConfirm(true);
      return;
    }

    // No references: proceed with normal delete (with progress)
    const deleted: { id: string; name: string }[] = [];
    const failed: { id: string; name: string; error: string }[] = [];

    if (deletableNow.length > 0) {
      let completed = 0;
      renderDeleteToast(completed, deletableNow.length);
      for (const a of deletableNow) {
        try {
          renderDeleteToast(completed, deletableNow.length, a.name);
          await deleteManualAsset(a.id);
          deleted.push({ id: a.id, name: a.name });
        } catch (err: any) {
          failed.push({ id: a.id, name: a.name, error: String(err?.message || '') });
        } finally {
          completed += 1;
          renderDeleteToast(completed, deletableNow.length);
        }
      }
      toast.dismiss(deleteToastIdRef.current);
    }

    const names = (arr: { name: string }[]) => arr.map(x => x.name);
    const listPreview = (arr: string[], max = 5) => arr.length <= max ? arr.join(', ') : `${arr.slice(0, max).join(', ')} +${arr.length - max} more`;

    if (failed.length) {
      const title = [`Deleted ${deleted.length}`, `Failed ${failed.length}`].filter(Boolean).join(' · ');
      const failedNames = listPreview(names(failed));
      toast.error(`${title}\nFailed: ${failedNames}`);
    } else if (deleted.length) {
      toast.success(`Deleted ${deleted.length} asset${deleted.length !== 1 ? 's' : ''}`);
    }

    setSelectedAssets([]);
    setShowDeleteConfirm(false);
    refetch();
  };

  const confirmDeleteManualOnly = async () => {
    const manualAssets = mixedManualAssetsRef.current || [];

    if ((manualAssets?.length || 0) === 0) {
      setShowMixedDeleteConfirm(false);
      return;
    }

    const allocationMap = await checkPortfolioAllocationsBulk(manualAssets.map(a => a.id));

    const blockedNow: { id: string; name: string; count: number; portfolios: string[] }[] = [];
    const deletableNow: { id: string; name: string }[] = [];

    for (const a of manualAssets) {
      const info = allocationMap[a.id];
      if (info && info.count > 0) {
        blockedNow.push({ id: a.id, name: (a as any).asset_name, count: info.count, portfolios: info.portfolioNames });
      } else {
        deletableNow.push({ id: a.id, name: (a as any).asset_name });
      }
    }

    setShowMixedDeleteConfirm(false);

    if (blockedNow.length > 0) {
      setCascadeBlocked(blockedNow);
      setCascadeDeletable(deletableNow);
      setCascadeShowAll(false);
      setShowCascadeConfirm(true);
      return;
    }

    const deleted: { id: string; name: string }[] = [];
    const failed: { id: string; name: string; error: string }[] = [];

    if (deletableNow.length > 0) {
      let completed = 0;
      renderDeleteToast(completed, deletableNow.length);
      for (const a of deletableNow) {
        try {
          renderDeleteToast(completed, deletableNow.length, a.name);
          await deleteManualAsset(a.id);
          deleted.push({ id: a.id, name: a.name });
        } catch (err: any) {
          failed.push({ id: a.id, name: a.name, error: String(err?.message || '') });
        } finally {
          completed += 1;
          renderDeleteToast(completed, deletableNow.length);
        }
      }
      toast.dismiss(deleteToastIdRef.current);
    }

    const names = (arr: { name: string }[]) => arr.map(x => x.name);
    const listPreview = (arr: string[], max = 5) => arr.length <= max ? arr.join(', ') : `${arr.slice(0, max).join(', ')} +${arr.length - max} more`;

    if (failed.length) {
      const title = [`Deleted ${deleted.length}`, `Failed ${failed.length}`].filter(Boolean).join(' · ');
      const failedNames = listPreview(names(failed));
      toast.error(`${title}\nFailed: ${failedNames}`);
    } else if (deleted.length) {
      toast.success(`Deleted ${deleted.length} asset${deleted.length !== 1 ? 's' : ''}`);
    }

    setSelectedAssets([]);
    refetch();
  };

  const confirmCascadeDelete = async () => {
    const deleted: { id: string; name: string }[] = [];
    const failed: { id: string; name: string; error: string }[] = [];

    const totalCount = cascadeDeletable.length + cascadeBlocked.length;
    let completed = 0;
    if (totalCount > 0) {
      renderDeleteToast(completed, totalCount);
    }

    for (const a of cascadeDeletable) {
      try {
        renderDeleteToast(completed, totalCount, a.name);
        await deleteManualAsset(a.id);
        deleted.push({ id: a.id, name: a.name });
      } catch (err: any) {
        failed.push({ id: a.id, name: a.name, error: String(err?.message || '') });
      } finally {
        completed += 1;
        renderDeleteToast(completed, totalCount);
      }
    }

    for (const b of cascadeBlocked) {
      try {
        renderDeleteToast(completed, totalCount, b.name);
        await deleteManualAsset(b.id, true);
        deleted.push({ id: b.id, name: b.name });
      } catch (err: any) {
        failed.push({ id: b.id, name: b.name, error: String(err?.message || '') });
      } finally {
        completed += 1;
        renderDeleteToast(completed, totalCount);
      }
    }

    const names = (arr: { name: string }[]) => arr.map(x => x.name);
    const listPreview = (arr: string[], max = 5) => arr.length <= max ? arr.join(', ') : `${arr.slice(0, max).join(', ')} +${arr.length - max} more`;

    if (failed.length) {
      const title = [`Deleted ${deleted.length}`, `Failed ${failed.length}`].filter(Boolean).join(' · ');
      const failedNames = listPreview(names(failed));
      toast.error(`${title}\nFailed: ${failedNames}`);
    } else {
      const cascadedCount = cascadeBlocked.length;
      toast.success(`Deleted ${deleted.length} asset${deleted.length !== 1 ? 's' : ''}${cascadedCount ? ` (removed from portfolios: ${cascadedCount})` : ''}`);
    }

    setCascadeBlocked([]);
    setCascadeDeletable([]);
    setSelectedAssets([]);
    setShowCascadeConfirm(false);
    toast.dismiss(deleteToastIdRef.current);
    refetch();
  };

  const handleClearSelection = () => setSelectedAssets([]);

  return {
    // state
    showDeleteConfirm,
    setShowDeleteConfirm,
    showMixedDeleteConfirm,
    setShowMixedDeleteConfirm,
    showCascadeConfirm,
    setShowCascadeConfirm,
    cascadeBlocked,
    cascadeDeletable,
    cascadeShowAll,
    setCascadeShowAll,
    mixedDeleteInfo,
    // actions
    handleBulkDelete,
    confirmDelete,
    confirmDeleteManualOnly,
    confirmCascadeDelete,
    handleClearSelection,
  };
}
